import axios from 'axios';

const isProduction = process.env.NODE_ENV === 'production';
const API_URL = isProduction ? 'https://catlas.net/api' : (process.env.REACT_APP_API_URL || 'http://localhost:5002/api');
const BASE_URL = isProduction ? 'https://catlas.net' : (process.env.REACT_APP_BASE_URL || 'http://localhost:5002');

const api = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth service
export const authService = {
  register: async (formData: FormData) => {
    const response = await api.post('/users/register', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  login: async (email: string, password: string) => {
    const response = await api.post('/users/login', { email, password });
    localStorage.setItem('token', response.data.token);
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  verify: async (token: string) => {
    const response = await api.get(`/users/verify?token=${token}`);
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await api.post('/users/resend-verification', { email });
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/users/me');
    return response.data;
  },

  updateProfile: async (formData: FormData) => {
    const response = await api.put('/users/me', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getMyCats: async () => {
    const response = await api.get('/users/me/cats');
    return response.data;
  },
};

// Cat service
export const catService = {
  getAll: async () => {
    const response = await api.get('/cats');
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/cats/${id}`);
    return response.data;
  },

  create: async (formData: FormData) => {
    const response = await api.post('/cats', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, data: any) => {
    const response = await api.put(`/cats/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/cats/${id}`);
    return response.data;
  },

  addPhoto: async (id: string, formData: FormData) => {
    const response = await api.post(`/cats/${id}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  setDefaultPhoto: async (catId: string, photoId: string) => {
    const response = await api.put(`/cats/${catId}/photos/${photoId}/set-default`);
    return response.data;
  },

  deletePhoto: async (catId: string, photoId: string) => {
    const response = await api.delete(`/cats/${catId}/photos/${photoId}`);
    return response.data;
  },
};

// Comment service
export const commentService = {
  getByCatId: async (catId: string) => {
    const response = await api.get(`/comments/cat/${catId}`);
    return response.data;
  },

  create: async (catId: string, content: string) => {
    const response = await api.post('/comments', { catId, content });
    return response.data;
  },

  update: async (id: string, content: string) => {
    const response = await api.put(`/comments/${id}`, { content });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/comments/${id}`);
    return response.data;
  },
};

// Admin service
export const adminService = {
  getAllUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  updateUser: async (id: string, formData: FormData) => {
    const response = await api.put(`/users/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteUser: async (id: string) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

// Utility functions
export const getImageUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}/${path}`;
};

export const hasValidLocation = (cat: any): boolean => {
  return (
    cat?.location &&
    typeof cat.location.latitude === 'number' &&
    typeof cat.location.longitude === 'number' &&
    !(cat.location.latitude === 0 && cat.location.longitude === 0)
  );
};

export default api;
